import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  ClipboardList, 
  Clock, 
  MapPin,
  Calendar,
  X,
  CheckCircle,
  AlertCircle,
  Loader2
} from "lucide-react";

const MyReservationsPage = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API}/my-reservations`, { withCredentials: true });
      setReservations(response.data);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (reservationId) => {
    setCancellingId(reservationId);
    try {
      await axios.post(
        `${API}/reservations/${reservationId}/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success("Reserva cancelada");
      fetchReservations();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al cancelar";
      toast.error(message);
    } finally {
      setCancellingId(null);
    }
  };

  const statusConfig = {
    active: { label: "Activa", color: "bg-green-100 text-green-700", icon: Clock },
    completed: { label: "Completada", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
    cancelled: { label: "Cancelada", color: "bg-slate-100 text-slate-500", icon: X },
    expired: { label: "Expirada", color: "bg-red-100 text-red-700", icon: AlertCircle }
  };

  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#374B67] mb-2" data-testid="reservations-title">
            Mis Reservas
          </h1>
          <p className="text-slate-600">
            Gestiona tus reservas activas y consulta el historial
          </p>
        </div>

        {reservations.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <ClipboardList className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">No tienes reservas</h3>
              <p className="text-slate-500">Explora el catálogo y reserva productos que te interesen</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reservations.map((reservation) => {
              const status = statusConfig[reservation.status];
              const StatusIcon = status.icon;
              const daysRemaining = getDaysRemaining(reservation.pickup_deadline);
              const isUrgent = reservation.status === "active" && daysRemaining <= 2;

              return (
                <Card key={reservation.reservation_id} className={`overflow-hidden ${isUrgent ? "border-red-200 bg-red-50/50" : ""}`} data-testid={`reservation-${reservation.reservation_id}`}>
                  <div className="flex flex-col sm:flex-row">
                    {/* Image */}
                    <div className="w-full sm:w-40 h-40 sm:h-auto flex-shrink-0">
                      <img
                        src={reservation.product_image || "https://via.placeholder.com/200"}
                        alt={reservation.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Content */}
                    <CardContent className="p-5 flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg text-[#374B67]">{reservation.product_name}</h3>
                          <p className="text-sm text-slate-500">
                            Reservado el {new Date(reservation.created_at).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric"
                            })}
                          </p>
                        </div>
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>

                      {reservation.status === "active" && (
                        <>
                          {/* Pickup Deadline */}
                          <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${isUrgent ? "bg-red-100" : "bg-slate-100"}`}>
                            <Calendar className={`w-5 h-5 ${isUrgent ? "text-red-600" : "text-slate-500"}`} />
                            <div>
                              <p className={`text-sm font-medium ${isUrgent ? "text-red-700" : "text-slate-700"}`}>
                                {isUrgent ? "¡Recógelo pronto!" : "Fecha límite de recogida"}
                              </p>
                              <p className={`text-xs ${isUrgent ? "text-red-600" : "text-slate-500"}`}>
                                {new Date(reservation.pickup_deadline).toLocaleDateString("es-ES", {
                                  weekday: "long",
                                  day: "numeric",
                                  month: "long"
                                })} ({daysRemaining > 0 ? `${daysRemaining} días restantes` : "Último día"})
                              </p>
                            </div>
                          </div>

                          {/* Location */}
                          <div className="flex items-start gap-2 mb-4 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 mt-0.5 text-slate-400" />
                            <div>
                              <p className="font-medium">Punto Limpio de Majadahonda</p>
                              <p className="text-slate-500">Carretera de El Plantío, 47, 28221</p>
                            </div>
                          </div>

                          {/* Cancel Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancel(reservation.reservation_id)}
                            disabled={cancellingId === reservation.reservation_id}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`cancel-btn-${reservation.reservation_id}`}
                          >
                            {cancellingId === reservation.reservation_id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Cancelando...
                              </>
                            ) : (
                              <>
                                <X className="w-4 h-4 mr-1" />
                                Cancelar Reserva
                              </>
                            )}
                          </Button>
                        </>
                      )}

                      {reservation.status === "completed" && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Producto recogido correctamente</span>
                        </div>
                      )}
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyReservationsPage;
